/**
 * URL de una variante WebP achicada de una imagen servida por `/api/file/proxy/…`.
 *
 * La API acepta `?w=` sólo con estos anchos y sólo para imágenes públicas (portadas,
 * categorías, marca); cualquier otra cosa la sirve tal cual. Una URL externa (firmada,
 * de otro host) o un asset propio vuelve sin tocar: no hay a quién pedirle la variante.
 *
 * NO usar para `og:image` ni JSON-LD: ahí conviene la imagen original.
 */
export const IMAGE_VARIANT_WIDTHS = [320, 640, 960, 1280] as const;
export type ImageVariantWidth = (typeof IMAGE_VARIANT_WIDTHS)[number];

const PROXY_PREFIX = "/api/file/proxy/";

export function isProxiedImage(url: string | null | undefined): url is string {
  return (
    !!url &&
    url.startsWith(PROXY_PREFIX) &&
    !url.startsWith(`${PROXY_PREFIX}_placeholder`) &&
    !url.includes("?")
  );
}

export function imageVariant<T extends string | null | undefined>(
  url: T,
  width: ImageVariantWidth,
): T | string {
  return isProxiedImage(url) ? `${url}?w=${width}` : url;
}

/** `srcSet` con los anchos dados, o `undefined` si la imagen no pasa por el proxy. */
export function imageVariantSrcSet(
  url: string | null | undefined,
  widths: readonly ImageVariantWidth[] = [640, 960, 1280],
): string | undefined {
  if (!isProxiedImage(url)) return undefined;
  return widths.map((w) => `${url}?w=${w} ${w}w`).join(", ");
}
